import { ChangeEvent, DragEvent, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import * as XLSX from "xlsx";
import "../../../assets/seleccionMasiva/carga-nomina-portales.scss";
import {
  PORTAL_SYSTEM_COLUMNS,
  PortalPayrollFieldKey,
  PortalResultFilter,
  PortalSheetData,
  extractPortalSheet,
  filterPortalResultRows,
  readPortalWorkbook,
  readPortalResultWorkbook,
  summarizePortalResult,
  suggestPortalMapping,
} from "./portalPayrollExcel";
import {
  PortalPayrollResult,
  downloadPortalPayrollResult,
  processPortalPayroll,
} from "../../../services/portalPayrollService";
import {
  PortalPayrollUpdateFilter,
  PortalPayrollUpdateResult,
  filterPortalPayrollUpdateRows,
  updatePortalPayrollRecords,
} from "../../../services/portalPayrollUpdateService";

interface CargaNominaPortalesProps {
  visible: boolean;
  onHide: () => void;
}

type PortalStep = "mapping" | "preview" | "result";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const PREVIEW_PAGE_SIZE = 100;

const DIACRITICS = new RegExp("[\u0300-\u036f]", "g");

const statusModifier = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(DIACRITICS, "")
    .replace(/[^a-z]+/g, "-")
    .replace(/^-|-$/g, "");

const CargaNominaPortales = ({ visible, onHide }: CargaNominaPortalesProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<PortalStep>("mapping");
  const [fileName, setFileName] = useState("");
  const [fileSize, setFileSize] = useState(0);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook>();
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [sheetData, setSheetData] = useState<PortalSheetData>();
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [draggedSource, setDraggedSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<PortalPayrollResult>();
  const [resultData, setResultData] = useState<PortalSheetData>();
  const [previewPage, setPreviewPage] = useState(0);
  const [resultPage, setResultPage] = useState(0);
  const [resultFilter, setResultFilter] = useState<PortalResultFilter>("all");
  const [updateConfirmVisible, setUpdateConfirmVisible] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState<PortalPayrollUpdateResult>();
  const [updateError, setUpdateError] = useState("");
  const [updateFilter, setUpdateFilter] = useState<PortalPayrollUpdateFilter>("updated");
  const [updatePage, setUpdatePage] = useState(0);

  const reset = () => {
    setStep("mapping");
    setFileName("");
    setFileSize(0);
    setWorkbook(undefined);
    setSheetNames([]);
    setSelectedSheet("");
    setSheetData(undefined);
    setMapping({});
    setSearch("");
    setDraggedSource("");
    setLoading(false);
    setError("");
    setResult(undefined);
    setResultData(undefined);
    setPreviewPage(0);
    setResultPage(0);
    setResultFilter("all");
    setUpdateConfirmVisible(false);
    setUpdating(false);
    setUpdateResult(undefined);
    setUpdateError("");
    setUpdateFilter("updated");
    setUpdatePage(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => {
    reset();
    onHide();
  };

  const selectSheet = (name: string, sourceWorkbook = workbook) => {
    setSelectedSheet(name);
    setError("");
    setResult(undefined);
    if (!name || !sourceWorkbook) {
      setSheetData(undefined);
      setMapping({});
      return;
    }
    try {
      const data = extractPortalSheet(sourceWorkbook, name);
      setSheetData(data);
      setMapping(suggestPortalMapping(data.headers));
    } catch (sheetError: any) {
      setSheetData(undefined);
      setMapping({});
      setError(sheetError?.message ?? "No se pudo leer la hoja seleccionada.");
    }
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    reset();
    if (!/\.xlsx?$/i.test(file.name)) {
      setError("Selecciona un archivo Excel con extensión .xlsx o .xls.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("El archivo supera el tamaño máximo permitido de 10 MB.");
      return;
    }
    try {
      const nextWorkbook = await readPortalWorkbook(file);
      setFileName(file.name);
      setFileSize(file.size);
      setWorkbook(nextWorkbook);
      setSheetNames(nextWorkbook.SheetNames);
      if (nextWorkbook.SheetNames.length === 1) selectSheet(nextWorkbook.SheetNames[0], nextWorkbook);
    } catch (_) {
      setError("No se pudo abrir el archivo. Verifica que sea un Excel válido y no esté protegido.");
    }
  };

  const assignSource = (target: PortalPayrollFieldKey, source: string) => {
    setMapping((current) => {
      const next = { ...current };
      Object.keys(next).forEach((key) => {
        if (next[key] === source && key !== target) delete next[key];
      });
      if (source) next[target] = source;
      else delete next[target];
      return next;
    });
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>, target: PortalPayrollFieldKey) => {
    event.preventDefault();
    const source = event.dataTransfer.getData("text/plain") || draggedSource;
    if (source) assignSource(target, source);
    setDraggedSource("");
  };

  const mappedSources = useMemo(() => new Set(Object.values(mapping)), [mapping]);
  const unmatchedHeaders = sheetData?.headers.filter((header) => !mappedSources.has(header)) ?? [];
  const visibleSystemColumns = PORTAL_SYSTEM_COLUMNS.filter((column) =>
    column.label.toLowerCase().includes(search.trim().toLowerCase())
  );
  const mappingPairs = PORTAL_SYSTEM_COLUMNS.flatMap((column) =>
    mapping[column.key] ? [{ source: mapping[column.key], target: column.key }] : []
  );
  const canReview = !!sheetData?.rows.length && !!mapping.rut;
  const previewTotalPages = Math.max(1, Math.ceil((sheetData?.rows.length ?? 0) / PREVIEW_PAGE_SIZE));
  const previewStart = previewPage * PREVIEW_PAGE_SIZE;
  const previewRows = sheetData?.rows.slice(previewStart, previewStart + PREVIEW_PAGE_SIZE) ?? [];
  const filteredResultRows = resultData ? filterPortalResultRows(resultData, resultFilter) : [];
  const resultTotalPages = Math.max(1, Math.ceil(filteredResultRows.length / PREVIEW_PAGE_SIZE));
  const resultStart = resultPage * PREVIEW_PAGE_SIZE;
  const resultRows = filteredResultRows.slice(resultStart, resultStart + PREVIEW_PAGE_SIZE);
  const resultRangeStart = filteredResultRows.length ? resultStart + 1 : 0;

  const selectResultFilter = (filter: PortalResultFilter) => {
    setResultFilter(filter);
    setResultPage(0);
  };

  const process = async () => {
    if (!sheetData || !selectedSheet || mappingPairs.length === 0) return;
    setLoading(true);
    setError("");
    try {
      const nextResult = await processPortalPayroll({
        fileName,
        fileSize,
        sheetName: selectedSheet,
        headerRow: sheetData.headerRow,
        mapping: mappingPairs,
        rows: sheetData.rows,
      });
      setResultPage(0);
      setResultFilter("all");
      try {
        const nextResultData = await readPortalResultWorkbook(nextResult.blob);
        setResultData(nextResultData);
        setResult({ ...nextResult, summary: summarizePortalResult(nextResultData) });
      } catch (_) {
        setResult(nextResult);
        setResultData(undefined);
        setError("El procesamiento terminó, pero no fue posible mostrar la vista previa. Puedes descargar el Excel de resultado.");
      }
      setStep("result");
    } catch (processError: any) {
      setError(processError?.message ?? "No fue posible procesar la nómina.");
    } finally {
      setLoading(false);
    }
  };

  const runUpdate = async () => {
    if (!result?.processId) return;
    setUpdating(true);
    setUpdateError("");
    try {
      const nextUpdate = await updatePortalPayrollRecords(result.processId);
      setUpdateResult(nextUpdate);
      setUpdateFilter(nextUpdate.summary.updated ? "updated" : "all");
      setUpdatePage(0);
      setUpdateConfirmVisible(false);
    } catch (updateFailure: any) {
      setUpdateError(updateFailure?.message ?? "No fue posible actualizar los registros.");
    } finally {
      setUpdating(false);
    }
  };

  const selectUpdateFilter = (filter: PortalPayrollUpdateFilter) => {
    setUpdateFilter(filter);
    setUpdatePage(0);
  };

  const updateRows = updateResult ? filterPortalPayrollUpdateRows(updateResult.rows, updateFilter) : [];
  const updateTotalPages = Math.max(1, Math.ceil(updateRows.length / PREVIEW_PAGE_SIZE));
  const updateStart = updatePage * PREVIEW_PAGE_SIZE;
  const visibleUpdateRows = updateRows.slice(updateStart, updateStart + PREVIEW_PAGE_SIZE);

  const renderHeader = () => (
    <div className="portal-payroll__titlebar">
      <div>
        <span className="portal-payroll__eyebrow">Carga flexible de Excel</span>
        <h2>Carga Nómina Portales</h2>
      </div>
      <button type="button" className="portal-payroll__icon-button" onClick={close} aria-label="Cerrar">
        <CloseIcon />
      </button>
    </div>
  );

  return (
    <Dialog
      header={renderHeader}
      visible={visible}
      onHide={close}
      closable={false}
      draggable={false}
      modal
      className="portal-payroll"
      style={{ width: "min(1180px, 96vw)", maxHeight: "calc(100vh - 24px)" }}
      contentStyle={{ padding: 0 }}
    >
      <div className="portal-payroll__steps" aria-label="Progreso">
        {["Archivo y match", "Revisión", "Resultado"].map((label, index) => {
          const activeIndex = step === "mapping" ? 0 : step === "preview" ? 1 : 2;
          return (
            <div className={`portal-payroll__step ${index <= activeIndex ? "is-active" : ""}`} key={label}>
              <span>{index + 1}</span>{label}
            </div>
          );
        })}
      </div>

      <div className="portal-payroll__body">
        {error && <div className="portal-payroll__alert"><ErrorOutlineIcon />{error}</div>}

        {step === "mapping" && (
          <>
            <section className="portal-payroll__file-row">
              <input ref={inputRef} type="file" accept=".xlsx,.xls" onChange={handleFile} hidden />
              <button type="button" className="portal-payroll__file-picker" onClick={() => inputRef.current?.click()}>
                {fileName ? <InsertDriveFileOutlinedIcon /> : <CloudUploadOutlinedIcon />}
                <span><strong>{fileName || "Seleccionar archivo Excel"}</strong><small>Formato .xlsx o .xls · máximo 10 MB</small></span>
              </button>
              <label className="portal-payroll__sheet-select">
                <span>Hoja a procesar</span>
                <select value={selectedSheet} onChange={(event) => selectSheet(event.target.value)} disabled={!workbook}>
                  <option value="">Selecciona una hoja</option>
                  {sheetNames.map((name) => <option value={name} key={name}>{name}</option>)}
                </select>
              </label>
              {sheetData && (
                <div className="portal-payroll__file-metadata">
                  <strong>{sheetData.rows.length.toLocaleString("es-CL")}</strong> registros
                  <span>Encabezado detectado en fila {sheetData.headerRow}</span>
                </div>
              )}
            </section>

            {sheetData && (
              <section className="portal-payroll__mapping">
                <div className="portal-payroll__mapping-panel">
                  <div className="portal-payroll__panel-heading">
                    <div><h3>Columnas de la hoja</h3><p>Arrastra una columna hacia su equivalente.</p></div>
                    <span>{unmatchedHeaders.length} sin match</span>
                  </div>
                  <div className="portal-payroll__source-list">
                    {sheetData.headers.map((header) => (
                      <button
                        type="button"
                        draggable
                        onDragStart={(event) => {
                          setDraggedSource(header);
                          event.dataTransfer.setData("text/plain", header);
                        }}
                        onDragEnd={() => setDraggedSource("")}
                        className={`portal-payroll__source ${mappedSources.has(header) ? "is-mapped" : ""}`}
                        key={header}
                      >
                        <span className="portal-payroll__drag-handle">⋮⋮</span>{header}
                        {mappedSources.has(header) && <CheckCircleOutlineIcon />}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="portal-payroll__mapping-panel portal-payroll__mapping-panel--system">
                  <div className="portal-payroll__panel-heading">
                    <div><h3>Columnas del sistema</h3><p>RUT es obligatorio. Cada columna puede usarse una sola vez.</p></div>
                    <span>{mappingPairs.length} matches</span>
                  </div>
                  <label className="portal-payroll__search"><SearchIcon /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar columna del sistema" /></label>
                  <div className="portal-payroll__target-list">
                    {visibleSystemColumns.map((column) => (
                      <div
                        className={`portal-payroll__target ${mapping[column.key] ? "is-mapped" : ""}`}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => handleDrop(event, column.key)}
                        key={column.key}
                      >
                        <div>
                          <strong>{column.label}{column.required && <em> *</em>}</strong>
                          {column.description && <small className="portal-payroll__target-description">{column.description}</small>}
                          <small className="portal-payroll__target-match">{mapping[column.key] || "Suelta una columna aquí"}</small>
                        </div>
                        <select value={mapping[column.key] || ""} onChange={(event) => assignSource(column.key, event.target.value)} aria-label={`Match para ${column.label}`}>
                          <option value="">Sin asignar</option>
                          {sheetData.headers.map((header) => (
                            <option value={header} disabled={mappedSources.has(header) && mapping[column.key] !== header} key={header}>{header}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {step === "preview" && sheetData && (
          <section className="portal-payroll__preview">
            <div className="portal-payroll__preview-heading">
              <div><h3>Revisa el match antes de procesar</h3><p>Vista de solo lectura con todas las filas, paginadas de {PREVIEW_PAGE_SIZE} en {PREVIEW_PAGE_SIZE}.</p></div>
              <span>{sheetData.rows.length.toLocaleString("es-CL")} registros</span>
            </div>
            <div className="portal-payroll__table-wrap">
              <table>
                <thead><tr>{mappingPairs.map(({ source, target }) => {
                  const label = PORTAL_SYSTEM_COLUMNS.find((column) => column.key === target)?.label;
                  return <th key={target}><span>{label}</span><small>{source}</small></th>;
                })}</tr></thead>
                <tbody>{previewRows.map((row, rowIndex) => (
                  <tr key={previewStart + rowIndex}>{mappingPairs.map(({ source, target }) => <td key={target}>{String(row[source] ?? "") || <em>Sin dato</em>}</td>)}</tr>
                ))}</tbody>
              </table>
            </div>
            <div className="portal-payroll__pagination">
              <span>
                Mostrando {previewStart + 1}–{Math.min(previewStart + PREVIEW_PAGE_SIZE, sheetData.rows.length)} de {sheetData.rows.length.toLocaleString("es-CL")}
              </span>
              <div>
                <button type="button" disabled={previewPage === 0} onClick={() => setPreviewPage((page) => Math.max(0, page - 1))}>Anterior</button>
                <strong>{previewPage + 1} / {previewTotalPages}</strong>
                <button type="button" disabled={previewPage + 1 >= previewTotalPages} onClick={() => setPreviewPage((page) => Math.min(previewTotalPages - 1, page + 1))}>Siguiente</button>
              </div>
            </div>
          </section>
        )}

        {step === "result" && result && (
          <section className="portal-payroll__result">
            <div className="portal-payroll__result-heading">
              <div className="portal-payroll__result-icon"><CheckCircleOutlineIcon /></div>
              <div>
                <h3>Nómina procesada</h3>
                <p>Revisa aquí el mismo contenido incluido en el Excel antes de descargarlo.</p>
                {result.processId && <small>ID de procesamiento: {result.processId}</small>}
              </div>
            </div>
            <div className="portal-payroll__summary">
              <button type="button" className={`portal-payroll__summary-card ${resultFilter === "all" ? "is-active" : ""}`} aria-pressed={resultFilter === "all"} onClick={() => selectResultFilter("all")}><strong>{result.summary.total}</strong><span>Total</span></button>
              <button type="button" className={`portal-payroll__summary-card complete ${resultFilter === "complete" ? "is-active" : ""}`} aria-pressed={resultFilter === "complete"} onClick={() => selectResultFilter("complete")}><strong>{result.summary.complete}</strong><span>Completos</span></button>
              <button type="button" className={`portal-payroll__summary-card incomplete ${resultFilter === "incomplete" ? "is-active" : ""}`} aria-pressed={resultFilter === "incomplete"} onClick={() => selectResultFilter("incomplete")}><strong>{result.summary.incomplete}</strong><span>Incompletos</span></button>
              <button type="button" className={`portal-payroll__summary-card missing ${resultFilter === "missing" ? "is-active" : ""}`} aria-pressed={resultFilter === "missing"} onClick={() => selectResultFilter("missing")}><strong>{result.summary.missing}</strong><span>Inexistentes</span></button>
              <button type="button" className={`portal-payroll__summary-card invalid ${resultFilter === "invalid" ? "is-active" : ""}`} aria-pressed={resultFilter === "invalid"} onClick={() => selectResultFilter("invalid")}><strong>{result.summary.invalid}</strong><span>Inválidos</span></button>
            </div>
            {resultData && (
              <>
                <div className="portal-payroll__preview-heading portal-payroll__result-table-heading">
                  <div><h3>Detalle del Excel generado</h3><p>Selecciona una tarjeta para filtrar esta vista. La descarga siempre incluye todos los registros.</p></div>
                  <span>{resultFilter === "all" ? `${resultData.rows.length.toLocaleString("es-CL")} registros` : `${filteredResultRows.length.toLocaleString("es-CL")} de ${resultData.rows.length.toLocaleString("es-CL")} registros`}</span>
                </div>
                <div className="portal-payroll__table-wrap portal-payroll__result-table">
                  <table>
                    <thead><tr>{resultData.headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
                    <tbody>{resultRows.map((row, rowIndex) => (
                      <tr key={resultStart + rowIndex}>{resultData.headers.map((header) => {
                        const value = String(row[header] ?? "");
                        const statusClass = header === "ESTADO REGISTRO"
                          ? ` portal-payroll__status--${statusModifier(value)}`
                          : "";
                        return <td className={statusClass} key={header}>{value || <em>Sin dato</em>}</td>;
                      })}</tr>
                    ))}
                    {resultRows.length === 0 && <tr><td className="portal-payroll__empty-result" colSpan={resultData.headers.length}>No hay registros para este filtro.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="portal-payroll__pagination">
                  <span>
                    Mostrando {resultRangeStart}–{Math.min(resultStart + PREVIEW_PAGE_SIZE, filteredResultRows.length)} de {filteredResultRows.length.toLocaleString("es-CL")}
                  </span>
                  <div>
                    <button type="button" disabled={resultPage === 0} onClick={() => setResultPage((page) => Math.max(0, page - 1))}>Anterior</button>
                    <strong>{resultPage + 1} / {resultTotalPages}</strong>
                    <button type="button" disabled={resultPage + 1 >= resultTotalPages} onClick={() => setResultPage((page) => Math.min(resultTotalPages - 1, page + 1))}>Siguiente</button>
                  </div>
                </div>
              </>
            )}

            {updateError && <div className="portal-payroll__alert"><ErrorOutlineIcon />{updateError}</div>}

            {updateConfirmVisible && !updateResult && (
              <div className="portal-payroll__update-confirm">
                <div className="portal-payroll__update-confirm-icon"><WarningAmberIcon /></div>
                <div>
                  <h3>Actualizar registros en Reclutamiento y Selección</h3>
                  <p>Se completarán los campos <strong>vacíos</strong> de las personas de esta nómina que existan en Reclutamiento y Selección, usando los datos del archivo.</p>
                  <ul>
                    <li>Nunca se sobrescribe un dato que ya tenga valor en el sistema.</li>
                    <li>No se crean personas: las filas <em>Inexistente</em> se omiten.</li>
                    <li>No se modifica Postulaciones, sólo Reclutamiento y Selección.</li>
                  </ul>
                  <div className="portal-payroll__update-confirm-actions">
                    <button type="button" className="portal-payroll__secondary" disabled={updating} onClick={() => setUpdateConfirmVisible(false)}>Cancelar</button>
                    <button type="button" className="portal-payroll__primary" disabled={updating} onClick={runUpdate}>{updating ? "Actualizando…" : "Confirmar actualización"}</button>
                  </div>
                </div>
              </div>
            )}

            {updateResult && (
              <div className="portal-payroll__update-result">
                <div className="portal-payroll__preview-heading">
                  <div>
                    <h3>Registros actualizados en Reclutamiento y Selección</h3>
                    <p>Se completaron {updateResult.summary.fieldsWritten.toLocaleString("es-CL")} campo(s) vacíos en {updateResult.summary.updated.toLocaleString("es-CL")} persona(s).</p>
                  </div>
                  <span>{updateRows.length.toLocaleString("es-CL")} de {updateResult.rows.length.toLocaleString("es-CL")} registros</span>
                </div>
                <div className="portal-payroll__summary">
                  <button type="button" className={`portal-payroll__summary-card complete ${updateFilter === "updated" ? "is-active" : ""}`} aria-pressed={updateFilter === "updated"} onClick={() => selectUpdateFilter("updated")}><strong>{updateResult.summary.updated}</strong><span>Actualizados</span></button>
                  <button type="button" className={`portal-payroll__summary-card invalid ${updateFilter === "issues" ? "is-active" : ""}`} aria-pressed={updateFilter === "issues"} onClick={() => selectUpdateFilter("issues")}><strong>{updateResult.summary.notFound + updateResult.summary.invalid + updateResult.summary.failed}</strong><span>Con observaciones</span></button>
                  <button type="button" className={`portal-payroll__summary-card ${updateFilter === "all" ? "is-active" : ""}`} aria-pressed={updateFilter === "all"} onClick={() => selectUpdateFilter("all")}><strong>{updateResult.summary.total}</strong><span>Total</span></button>
                </div>
                <div className="portal-payroll__table-wrap portal-payroll__result-table">
                  <table>
                    <thead><tr><th>Fila</th><th>RUT</th><th>Resultado</th><th>Campos completados</th><th>Valores no reconocidos</th><th>Detalle</th></tr></thead>
                    <tbody>
                      {visibleUpdateRows.map((row) => (
                        <tr key={`${row.rowNumber}-${row.rut}`}>
                          <td>{row.rowNumber}</td>
                          <td>{row.rut || <em>Sin dato</em>}</td>
                          <td className={`portal-payroll__status--${statusModifier(row.status)}`}>{row.status}</td>
                          <td>{row.updatedFields.join(", ") || <em>Sin dato</em>}</td>
                          <td>{row.unresolvedValues.join("; ") || <em>Sin dato</em>}</td>
                          <td>{row.description}</td>
                        </tr>
                      ))}
                      {visibleUpdateRows.length === 0 && <tr><td className="portal-payroll__empty-result" colSpan={6}>No hay registros para este filtro.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="portal-payroll__pagination">
                  <span>
                    Mostrando {updateRows.length ? updateStart + 1 : 0}–{Math.min(updateStart + PREVIEW_PAGE_SIZE, updateRows.length)} de {updateRows.length.toLocaleString("es-CL")}
                  </span>
                  <div>
                    <button type="button" disabled={updatePage === 0} onClick={() => setUpdatePage((page) => Math.max(0, page - 1))}>Anterior</button>
                    <strong>{updatePage + 1} / {updateTotalPages}</strong>
                    <button type="button" disabled={updatePage + 1 >= updateTotalPages} onClick={() => setUpdatePage((page) => Math.min(updateTotalPages - 1, page + 1))}>Siguiente</button>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}
      </div>

      <footer className="portal-payroll__footer">
        {step === "mapping" && <><button type="button" className="portal-payroll__secondary" onClick={close}>Cancelar</button><button type="button" className="portal-payroll__primary" disabled={!canReview} onClick={() => { setError(""); setPreviewPage(0); setStep("preview"); }}>Revisar match</button></>}
        {step === "preview" && <><button type="button" className="portal-payroll__secondary" onClick={() => setStep("mapping")}><ArrowBackIcon />Volver al match</button><button type="button" className="portal-payroll__primary" disabled={loading} onClick={process}>{loading ? "Procesando…" : "Procesar y generar Excel"}</button></>}
        {step === "result" && result && <><button type="button" className="portal-payroll__secondary" onClick={close}>Cerrar</button>{!updateResult && <button type="button" className="portal-payroll__update-action" disabled={updating || !result.processId} onClick={() => { setUpdateError(""); setUpdateConfirmVisible(true); }}><PublishedWithChangesIcon />{updating ? "Actualizando…" : "Actualizar registros"}</button>}<button type="button" className="portal-payroll__primary portal-payroll__download-result" onClick={() => downloadPortalPayrollResult(result)}><DownloadIcon />Descargar Excel de resultado</button></>}
      </footer>
    </Dialog>
  );
};

export default CargaNominaPortales;
